import { createContext, useContext, useState, useCallback, useEffect } from 'react'
import { X } from 'lucide-react'

const ToastContext = createContext(null)

let idSeq = 1

export function ToastProvider({ children }) {
  const [toasts, setToasts] = useState([])

  const push = useCallback((message, opts = {}) => {
    const id = idSeq++
    const tone = opts.tone || 'info' // 'info' | 'success' | 'error' | 'warning'
    const duration = opts.duration ?? 4000
    setToasts((t) => [...t, { id, message, tone }])
    if (duration > 0) {
      setTimeout(() => setToasts((t) => t.filter((x) => x.id !== id)), duration)
    }
    return id
  }, [])

  const dismiss = useCallback(
    (id) => setToasts((t) => t.filter((x) => x.id !== id)),
    [],
  )

  const api = {
    info: (msg, opts) => push(msg, { ...opts, tone: 'info' }),
    success: (msg, opts) => push(msg, { ...opts, tone: 'success' }),
    error: (msg, opts) => push(msg, { ...opts, tone: 'error' }),
    warning: (msg, opts) => push(msg, { ...opts, tone: 'warning' }),
    dismiss,
  }

  return (
    <ToastContext.Provider value={api}>
      {children}
      <div className="fixed bottom-4 inset-x-4 sm:bottom-6 sm:right-6 sm:left-auto sm:max-w-sm z-50 flex flex-col gap-2 pointer-events-none">
        {toasts.map((t) => (
          <ToastItem key={t.id} toast={t} onDismiss={() => dismiss(t.id)} />
        ))}
      </div>
    </ToastContext.Provider>
  )
}

function ToastItem({ toast, onDismiss }) {
  const [leaving, setLeaving] = useState(false)
  useEffect(() => {
    /* lifecycle managed by provider; this hook is to hint future animation */
  }, [])
  const tones = {
    info: 'bg-blue-600 text-white',
    success: 'bg-green-600 text-white',
    error: 'bg-red-600 text-white',
    warning: 'bg-amber-500 text-white',
  }
  return (
    <div
      className={`pointer-events-auto rounded-md shadow-lg px-4 py-3 text-sm transition-opacity ${
        tones[toast.tone] || tones.info
      } ${leaving ? 'opacity-0' : 'opacity-100'}`}
      role="status"
      aria-live="polite"
    >
      <div className="flex items-start gap-3">
        <div className="flex-1">{toast.message}</div>
        <button
          onClick={() => {
            setLeaving(true)
            onDismiss()
          }}
          aria-label="Chiudi"
          className="opacity-80 hover:opacity-100"
        >
          <X size={16} aria-hidden="true" />
        </button>
      </div>
    </div>
  )
}

export function useToast() {
  const ctx = useContext(ToastContext)
  if (!ctx) {
    // Fallback se ToastProvider non è montato (testing / componente isolato)
    return {
      info: (msg) => alert(msg),
      success: (msg) => alert(msg),
      error: (msg) => alert(msg),
      warning: (msg) => alert(msg),
      dismiss: () => {},
    }
  }
  return ctx
}
